import * as React from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import Dialog from 'material-ui/Dialog';
import { ProgramMemberInfo } from './members.types';
import { Program, ProgramsResponseData, ProgramsState, ProgramsDetailsMode } from '../programs/Types';
import { initialProgramMemberDetail } from './members.constants';
import ProgramMemberTableComponent from './programMember.table.component';
import ProgramMemberDetailsComponent from './programMember.details.component';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import { schedulesComparator } from '../util/compare-util';
import {
  NOT_ENOUGH_PERMISSION_IMAGE,
  isProgramDisabled,
  cannotWrite,
  cannotDelete
} from '../util/permission.util';
import { BusinessState } from '../business/business.types';
import { PROGRAM } from '../account/Constants';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import SectionPanel from '../util/SectionPanelComponent';

const customDialogContentStyle = {
  width: '100%',
  maxWidth: '1200px',
};

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly businessState: BusinessState
  readonly programsState: ProgramsState
  readonly programMembers: ProgramMemberInfo[]
  readonly onProgramMembersChangeHandler: Function
  readonly maxAttendancePerWeek: number
  readonly getPrograms: () => Promise<ProgramsResponseData>
};

type States = {
  readonly programMembers: ProgramMemberInfo[]
  readonly allPrograms: Program[]
  readonly detailDisplayProgramMember: ProgramMemberInfo
  readonly showProgramDetailsModal: boolean
  readonly programDetailsMode: ProgramsDetailsMode
  readonly loading: boolean
};

class ProgramMemberComponent extends React.Component<Props, States> {
  constructor(props: Props) {
    super(props);

    this.state = {
      programMembers: this.props.programMembers,
      allPrograms: [],
      detailDisplayProgramMember: JSON.parse(JSON.stringify(initialProgramMemberDetail)),
      showProgramDetailsModal: false,
      programDetailsMode: 'CREATE',
      loading: false
    };

    this.loadProgramsList = this.loadProgramsList.bind(this);
    this.toggleProgramDetailsModal = this.toggleProgramDetailsModal.bind(this);
    this.programClickHandler = this.programClickHandler.bind(this);
    this.programMemberClickHandler = this.programMemberClickHandler.bind(this);
    this.onRemoveProgram = this.onRemoveProgram.bind(this);
    this.onSubmitProgram = this.onSubmitProgram.bind(this);
    this.isValidProgramDetail = this.isValidProgramDetail.bind(this);
    this.programMembersChangeCallback = this.programMembersChangeCallback.bind(this);
    this.programMemberDetailChangeHandler = this.programMemberDetailChangeHandler.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setState({
      programMembers: nextProps.programMembers
    });  
  }

  render() {
    return (
      <div>
        <div className="row">
          <div className="col-12">
            {this.renderProgramMember()}
          </div>
          <Dialog
            title="Programs"
            actions={[this.renderCloseButton()]}
            open={this.state.showProgramDetailsModal}
            modal={true}
            contentStyle={customDialogContentStyle}
            autoScrollBodyContent={true}
          >
            <div className="row">
              <div className="col-6">
                <ProgramMemberTableComponent 
                  programs={this.state.allPrograms}
                  programClickHandler={this.programClickHandler}
                />
              </div>
              <div className="col-6">  
                <div className="row pt-4">    
                  <div className="col-12">
                    <h3 className="float-left">Detail</h3>
                  </div>
                  {
                    (this.state.detailDisplayProgramMember.program.id !== 0)
                    ? (
                      <div className="col-12">
                        <ProgramMemberDetailsComponent
                          programMember={this.state.detailDisplayProgramMember}
                          programMemberDetailChangeHandler={this.programMemberDetailChangeHandler}
                        />
                        <RaisedButton
                          label="Submit"
                          primary={true}
                          className="float-right"
                          disabled={!this.isValidProgramDetail()}
                          onClick={this.onSubmitProgram}
                        />
                        {
                          (this.state.programDetailsMode === 'EDIT') ? 
                          <RaisedButton
                            label="Remove"
                            secondary={true}
                            className="float-right mr-2"
                            onClick={this.onRemoveProgram}
                            disabled={cannotDelete(PROGRAM, this.props.authState, this.props.adminState)}
                          /> :
                          null
                        }
                      </div>
                    ) : null
                  }
                </div>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    );
  }

  private renderProgramMember = (): JSX.Element => {
    if (isProgramDisabled(this.props.authState, this.props.adminState)) {
      return(<img src={NOT_ENOUGH_PERMISSION_IMAGE} className="img-responsive img-thumbnail"/>);
    } else {
      return(
        <ProgramMemberTableComponent
          programs={this.state.programMembers}
          // programClickHandler={this.programMemberClickHandler}
        />
      );
    }
  }

  private loadProgramsList() {
    this.setState(
      {loading: !isAuthTokenExpired()},
      () => {
        this.props.getPrograms()
        .then((programsResponseData) => {
          this.setState(
            {loading: false}
          );
          
          switch (this.props.programsState.statusCode) {
            case 404:
              return;
            case 200:
              this.setState(
                {
                  allPrograms: this.props.programsState.programs
                },
                (this.state.showProgramDetailsModal) ? () => null : this.toggleProgramDetailsModal);
              return;
            default:
              return;
          }
        });
      }
    );
  }

  private toggleProgramDetailsModal() {
    if (this.state.programDetailsMode === 'CREATE') {
      let displayProgramMember: ProgramMemberInfo = JSON.parse(JSON.stringify(initialProgramMemberDetail));

      this.setState({
        detailDisplayProgramMember: displayProgramMember
      });
    }
    
    this.setState({
      showProgramDetailsModal: !this.state.showProgramDetailsModal
    });
  }

  private programClickHandler(clickedProgram: Program) {
    let existingPrograms = this.state.programMembers.filter((programMember: ProgramMemberInfo) => {
      return programMember.program.id === clickedProgram.id;
    });

    let clickedProgramMember: ProgramMemberInfo;
    if (existingPrograms.length > 0) {
      clickedProgramMember = JSON.parse(JSON.stringify(existingPrograms[0]));
    } else {
      clickedProgramMember = JSON.parse(JSON.stringify(initialProgramMemberDetail));
      clickedProgramMember.program = JSON.parse(JSON.stringify(clickedProgram));
    }

    clickedProgramMember.program.schedules.sort(schedulesComparator);

    this.setState({
      detailDisplayProgramMember: clickedProgramMember,
      programDetailsMode: (existingPrograms.length > 0) ? 'EDIT' : 'CREATE'
    });
  }

  private programMemberClickHandler(clickedProgramMember: ProgramMemberInfo) {
    let sortedProgramMember = JSON.parse(JSON.stringify(clickedProgramMember));
    sortedProgramMember.program.schedules.sort(schedulesComparator);

    if (cannotWrite(PROGRAM, this.props.authState, this.props.adminState)) {
      return;
    } else {
      this.setState(
        {
          detailDisplayProgramMember: sortedProgramMember,
          programDetailsMode: 'EDIT'
        },
        this.loadProgramsList
      );
    }
  }

  private onRemoveProgram() {
    let newProgramMembers = this.state.programMembers;
    for (let i = 0; i < newProgramMembers.length; i++) {
      if (newProgramMembers[i].program.id === this.state.detailDisplayProgramMember.program.id) {
        newProgramMembers[i] = {
          ...newProgramMembers[i],
          isDeleted: true
        };
      }
    }

    this.setState(
      {
        programMembers: newProgramMembers,
        detailDisplayProgramMember: JSON.parse(JSON.stringify(initialProgramMemberDetail))
      },
      this.programMembersChangeCallback
    );
  }

  private onSubmitProgram() {
    if (!this.isValidProgramDetail()) {
      return;
    }

    let isUpdate = false;
    let newProgramMembers = this.state.programMembers;
    for (let i = 0; i < newProgramMembers.length; i++) {
      if (newProgramMembers[i].program.id === this.state.detailDisplayProgramMember.program.id) {
        newProgramMembers[i] = JSON.parse(JSON.stringify(this.state.detailDisplayProgramMember));
        isUpdate = true;
      }
    }

    if (!isUpdate) {
      newProgramMembers.push(JSON.parse(JSON.stringify(this.state.detailDisplayProgramMember)));
    }

    this.setState(
      {
        programMembers: newProgramMembers,
        detailDisplayProgramMember: JSON.parse(JSON.stringify(initialProgramMemberDetail))
      },
      this.programMembersChangeCallback
    );
  }

  private isValidProgramDetail() {
    const { maxAttendancePerWeek } = this.props;
    const { detailDisplayProgramMember } = this.state;

    return detailDisplayProgramMember.level !== undefined &&
      detailDisplayProgramMember.level.id !== 0 &&
      (maxAttendancePerWeek > 0 || detailDisplayProgramMember.schedules.length > 0);
  }

  private programMembersChangeCallback() {
    this.props.onProgramMembersChangeHandler();
  }

  private programMemberDetailChangeHandler() {
    this.setState({
      detailDisplayProgramMember: this.state.detailDisplayProgramMember
    });
  }

  private renderCloseButton = (): JSX.Element => {
    return(
      <RaisedButton 
        label="Close"
        primary={true}
        keyboardFocused={true}
        className="mr-2"
        onClick={() => this.toggleProgramDetailsModal()}
      />
    );
  }
}

export default ProgramMemberComponent;



// WEBPACK FOOTER //
// ./src/members/programMember.component.tsx